import { useDraggable, useDroppable } from "@dnd-kit/core";
import {
	SideMenuItemContent,
	type SideMenuItemContentProps,
} from "@ext/navigation/catalog/SidebarNavigation/components/Helpers/SideMenuItemContent";
import { type MutableRefObject, type ReactNode, useCallback } from "react";

interface ItemDroppableProps {
	id: string;
	children: ReactNode;
	itemRef?: MutableRefObject<HTMLDivElement>;
}

export const ItemDroppable = ({ id, children, itemRef }: ItemDroppableProps) => {
	const { setNodeRef } = useDroppable({ id });
	const setRef = useCallback(
		(element: HTMLDivElement | null) => {
			if (itemRef) itemRef.current = element;
			setNodeRef(element);
		},
		[itemRef, setNodeRef],
	);

	return (
		<div className="relative" ref={setRef}>
			{children}
		</div>
	);
};

type DraggableSideMenuItemContentProps = Omit<
	SideMenuItemContentProps,
	"draggableRef" | "dragListeners" | "dragAttributes"
> & {
	id: string;
	/** Nothing on the row works meanwhile: dnd-kit marks it `aria-disabled`, which the row styles as disabled. */
	disabled: boolean;
	/** Only dragging is off; the row and its actions stay in use. */
	undraggable?: boolean;
};

export const DraggableSideMenuItemContent = ({
	id,
	disabled,
	undraggable,
	...props
}: DraggableSideMenuItemContentProps) => {
	const { attributes, listeners, setNodeRef } = useDraggable({ id, disabled: disabled || undraggable });

	return (
		<SideMenuItemContent
			{...props}
			dragAttributes={undraggable && !disabled ? undefined : attributes}
			draggableRef={setNodeRef}
			dragListeners={disabled ? undefined : listeners}
		/>
	);
};
